/**
 * This custom hook provides the functionality to save a user with the given parameters.
 */

import React, {useEffect} from "react";
import useFetchSelectableFilterItems from "./useFetchSelectableFilterItems";
import {subDays} from 'date-fns';
import {useConfirm} from "material-ui-confirm";
import StatisticsDateRangePicker from "../utils/StatisticsDateRangePicker";
import {useIntl} from "react-intl";
import useFetchChartData from "./useFetchChartData";

const useGeneralEditPlaylistForm = () => {

    // Confirmation dialog
    const confirm = useConfirm();

    // Hook for calculating selectable filter entries and fetching the statistics based on the provided parameters
    const {
        selectionBasis,
        getSelectableAssociationItems,
        getSelectableDifficultyItems,
        getSelectableDisciplineItems,
        getSelectableCourseItems,
        getSelectablePlaylistItems,
        getSelectableUserItems,
        getSelectableExpertiseItems
    } = useFetchSelectableFilterItems()

    // Hook for querying the backend for the statistical data based on the provided parameters
    const {fetchChartData} = useFetchChartData()

    // States holding the currently filter criteria (all fields are multi-select)
    const [associationsFilter, setAssociationsFilter] = React.useState([]);
    const [disciplinesFilter, setDisciplinesFilter] = React.useState([]);
    const [coursesFilter, setCoursesFilter] = React.useState([]);
    const [playlistsFilter, setPlaylistsFilter] = React.useState([]);
    const [usersFilter, setUsersFilter] = React.useState([]);
    const [expertisesFilter, setExpertisesFilter] = React.useState([]);
    const [difficultiesFilter, setDifficultiesFilter] = React.useState([]);
    const [fromToDateRange, setFromToDateRange] = React.useState([subDays(new Date(), 365), new Date()])

    // States holding the selectable data for the filter multi-selects
    const [selectableAssociations, setSelectableAssociations] = React.useState([]);
    const [selectableDisciplines, setSelectableDisciplines] = React.useState([]);
    const [selectableCourses, setSelectableCourses] = React.useState([]);
    const [selectablePlaylists, setSelectablePlaylists] = React.useState([]);
    const [selectableUsers, setSelectableUsers] = React.useState([]);
    const [selectableExpertises, setSelectableExpertises] = React.useState([]);
    const [selectableDifficulties, setSelectableDifficulties] = React.useState([]);

    // States holding the information how to generate the chart
    const [metric, setMetric] = React.useState('decisionAccuracy');
    const [aggregation, setAggregation] = React.useState('association');
    const [chartType, setChartType] = React.useState('total');

    // States holding the currently shown chart type (not the values selected in the UI elements)
    const [visibleMetric, setVisibleMetric] = React.useState('');
    const [visibleAggregation, setVisibleAggregation] = React.useState('');
    const [visibleChartType, setVisibleChartType] = React.useState('');

    // The allowed selection setup: used to reset invalid selections, restrict chart types, restrict filters, avoid duplicate statistical fetches
    const validSelectionSetup = {
        decisionAccuracy: {
            aggregations: ['association', 'discipline', 'course', 'playlist', 'scoredScene', 'difficulty', 'user', 'expertise', 'technique', 'score', 'athlete'],
            chartTypes: ['total', 'average', 'accumulated'],
            filters: ['associations', 'disciplines', 'courses', 'playlists', 'users', 'expertises', 'difficulties']
        },
        reactionTime: {
            aggregations: ['association', 'discipline', 'course', 'playlist', 'scoredScene', 'difficulty', 'user', 'expertise', 'technique', 'score', 'athlete'],
            chartTypes: ['average', 'accumulated'],
            filters: ['associations', 'disciplines', 'courses', 'playlists', 'users', 'expertises', 'difficulties']
        },
        trainingIntensity: {
            aggregations: ['association', 'discipline', 'course', 'playlist', 'scoredScene', 'difficulty', 'user', 'expertise'],
            chartTypes: ['total', 'average', 'accumulated'],
            filters: ['associations', 'disciplines', 'courses', 'playlists', 'users', 'expertises', 'difficulties']
        },
        createdScoredScenes: {
            aggregations: ['association', 'discipline', 'difficulty', 'scoredSceneStatus', 'scoredSceneGender', 'scoredSceneAgeGroup', 'user'],
            chartTypes: ['total', 'accumulated'],
            filters: ['associations', 'disciplines', 'users', 'difficulties']
        },
        createdUsers: {
            aggregations: ['association', 'discipline', 'expertise'],
            chartTypes: ['total', 'accumulated'],
            filters: ['associations', 'disciplines', 'expertises']
        },
        createdChallengedDecisions: {
            aggregations: ['association', 'discipline', 'course', 'playlist', 'scoredScene', 'difficulty', 'user', 'expertise'],
            chartTypes: ['total'],
            filters: ['associations', 'disciplines', 'courses', 'playlists', 'users', 'expertises', 'difficulties']
        }
    }

    // List of selectable aggregation levels (restricted in case of training intensity metric)
    const [selectableAggregations, setSelectableAggregations] = React.useState([
        {
            group: 'groupScoredScene',
            values: ['association', 'discipline', 'course', 'playlist', 'scoredScene', 'difficulty']
        },
        {
            group: 'groupUser',
            values: ['user', 'expertise']
        },
        {
            group: 'groupDecision',
            values: ['technique', 'score', 'athlete']
        }
    ])

    // Internationalization for simple strings
    const intl = useIntl();

    /*
     Updating the selectable multi-select items after each selection-update.
     IMPORTANT: The "getSelectable..." functions are designed in a way that no selection means "all".
                Therefore, all values are available from the very beginning!
     */
    useEffect(() => {
        if (selectionBasis) {
            Promise.all([
                getSelectableAssociationItems(),
                getSelectableDisciplineItems(associationsFilter.map(a => a.id)),
                getSelectableCourseItems(associationsFilter.map(a => a.id), disciplinesFilter.map(d => d.id)),
                getSelectablePlaylistItems(associationsFilter.map(a => a.id), disciplinesFilter.map(d => d.id), coursesFilter.map(c => c.id)),
                getSelectableUserItems(associationsFilter.map(a => a.id), disciplinesFilter.map(d => d.id), coursesFilter.map(c => c.id), metric),
                getSelectableExpertiseItems(associationsFilter.map(a => a.id), disciplinesFilter.map(d => d.id), coursesFilter.map(c => c.id), usersFilter.map(u => u.id), metric),
                getSelectableDifficultyItems()
            ]).then((results) => {
                setSelectableAssociations(results[0])
                setSelectableDisciplines(results[1])
                setSelectableCourses(results[2])
                setSelectablePlaylists(results[3])
                setSelectableUsers(results[4])
                setSelectableExpertises(results[5])
                setSelectableDifficulties(results[6])
            })
        }
    }, [selectionBasis, metric, associationsFilter, disciplinesFilter, coursesFilter, usersFilter])

    // Set the chart type default value (= active toggle buttons) in case the metric value changes
    useEffect(() => {
        if (metric === 'decisionAccuracy') {
            setSelectableAggregations([
                    {
                        group: 'groupScoredScene',
                        values: ['association', 'discipline', 'course', 'playlist', 'scoredScene', 'difficulty']
                    },
                    {
                        group: 'groupUser',
                        values: ['user', 'expertise']
                    },
                    {
                        group: 'groupDecision',
                        values: ['technique', 'score', 'athlete']
                    }
                ]
            )
        }
        if (metric === 'reactionTime') {
            setSelectableAggregations([
                {
                    group: 'groupScoredScene',
                    values: ['association', 'discipline', 'course', 'playlist', 'scoredScene', 'difficulty']
                },
                {
                    group: 'groupUser',
                    values: ['user', 'expertise']
                },
                {
                    group: 'groupDecision',
                    values: ['technique', 'score', 'athlete']
                }
            ])
        }
        if (metric === 'trainingIntensity') {
            setSelectableAggregations([
                {
                    group: 'groupScoredScene',
                    values: ['association', 'discipline', 'course', 'playlist', 'scoredScene', 'difficulty']
                },
                {
                    group: 'groupUser',
                    values: ['user', 'expertise']
                }
            ])
        }
        if (metric === 'createdScoredScenes') {
            setSelectableAggregations([
                {
                    group: 'groupScoredScene',
                    values: ['association', 'discipline', 'difficulty', 'scoredSceneStatus', 'scoredSceneGender', 'scoredSceneAgeGroup']
                },
                {
                    group: 'groupUser',
                    values: ['user']
                }
            ])
        }
        if (metric === 'createdUsers') {
            setSelectableAggregations([
                {
                    group: 'groupUser',
                    values: ['association', 'discipline', 'expertise']
                }
            ])
        }
        if (metric === 'createdChallengedDecisions') {
            setSelectableAggregations([
                {
                    group: 'groupScoredScene',
                    values: ['association', 'discipline', 'course', 'playlist', 'scoredScene', 'difficulty']
                },
                {
                    group: 'groupUser',
                    values: ['user', 'expertise']
                }
            ])
        }
    }, [metric])

    // Resets to prevent unsupported selections
    useEffect(() => {

        // Reset the chart type if it is not supported by the selected metric
        if (!validSelectionSetup[metric].chartTypes.includes(chartType)) {
            setChartType(validSelectionSetup[metric].chartTypes[0])
        }

        // Reset the aggregation if it is not supported by the selected metric
        if (!validSelectionSetup[metric].aggregations.includes(aggregation)) {
            setAggregation(validSelectionSetup[metric].aggregations[0])
        }
    }, [metric, chartType, aggregation])

    // Handlers for setting the state after changing a filter criteria
    const handleChangeAssociationFilter = (event, value) => {
        setAssociationsFilter(value)
    }

    const handleChangeDisciplinesFilter = (event, value) => {
        setDisciplinesFilter(value)
    }

    const handleChangeCoursesFilter = (event, value) => {
        setCoursesFilter(value)
    }

    const handleChangePlaylistsFilter = (event, value) => {
        setPlaylistsFilter(value)
    }

    const handleChangeUsersFilter = (event, value) => {
        setUsersFilter(value)
    }

    const handleChangeExpertisesFilter = (event, value) => {
        setExpertisesFilter(value)
    }

    const handleChangeDifficultyFilter = (event, value) => {
        setDifficultiesFilter(value)
    }

    const handlePickDate = () => {

        let selectedStartDate
        let selectedEndDate

        confirm({
            title: intl.formatMessage({
                id: 'app.statistics.dialog.dateRange.selection.title',
                defaultMessage: 'Choose time range...'
            }),
            content: <StatisticsDateRangePicker
                startDate={fromToDateRange[0]}
                endDate={fromToDateRange[1]}
                onPeriodChanged={(startDate, endDate) => {
                    selectedStartDate = startDate
                    selectedEndDate = endDate
                }
                }/>
        }).then(() => {

            // IMPORTANT: When selecting nothing, but clicking simply on OK, selectedStartDate and selectedEndDate is undefined
            // IMPORTANT: the timestamps are always at "start date 00:00:00 UTC" - "end date 23:59:59 UTC"
            if (selectedStartDate && selectedEndDate) {
                setFromToDateRange([selectedStartDate, selectedEndDate])
            }
        }).catch(() => null)
    }

    // States for updating chart-specific data
    const handleChangeMetric = (event) => {
        setMetric(event.target.value)
    }

    const handleChangeAggregation = (event) => {
        setAggregation(event.target.value)
    }

    const handleChangeChartType = (event, value) => {
        // Go sure that exactly one toggle button is always active
        if (value !== null) {
            setChartType(value)
        }
    }

    const isSelectionAllowed = (metric, aggregation, chartType) => {
        return validSelectionSetup[metric].aggregations.includes(aggregation) && validSelectionSetup[metric].chartTypes.includes(chartType)
    }

    // Freshly fetch the statistical data based on the given filter criteria and the chart-specific information
    const getDiagramData = () => {

        // "Reset" visible selection
        setVisibleMetric('')
        setVisibleAggregation('')
        setVisibleChartType('')

        return fetchChartData(
            associationsFilter,
            disciplinesFilter,
            coursesFilter,
            playlistsFilter,
            usersFilter,
            expertisesFilter,
            difficultiesFilter,
            fromToDateRange[0],
            fromToDateRange[1],
            metric,
            aggregation,
            chartType)
            .then((result) => {
                /* If the result is undefined (which can only happen if some data inconsistency occurs) we need to interrupt here.
                   If this is the case, the fetchChartData already care that the error message is shown to the user. */
                if (!result) {
                    return;
                }

                // Set visible selection
                setVisibleMetric(metric)
                setVisibleAggregation(aggregation)
                setVisibleChartType(chartType)

                return result
            })
    }

    return {
        associationsFilter,
        disciplinesFilter,
        coursesFilter,
        playlistsFilter,
        usersFilter,
        expertisesFilter,
        difficultiesFilter,

        selectableAssociations,
        selectableDisciplines,
        selectableCourses,
        selectablePlaylists,
        selectableUsers,
        selectableExpertises,
        selectableDifficulties,
        selectableAggregations,

        metric,
        aggregation,
        fromToDateRange,
        chartType,

        visibleMetric,
        visibleAggregation,
        visibleChartType,

        handleChangeAssociationFilter,
        handleChangeDisciplinesFilter,
        handleChangeCoursesFilter,
        handleChangePlaylistsFilter,
        handleChangeUsersFilter,
        handleChangeExpertisesFilter,
        handleChangeDifficultyFilter,

        handleChangeMetric,
        handleChangeAggregation,
        handleChangeChartType,

        getDiagramData,

        handlePickDate,

        validSelectionSetup,
        isSelectionAllowed
    }
}
export default useGeneralEditPlaylistForm;